/**
 * Visuel de la fiche produit : les pack-shots du tissu affiché — un par
 * piètement — puis les photos d'ambiance de la variante servie, en boucle.
 *
 * ⚠ LE LIEN AVEC LE PIÈTEMENT NE VA QUE DANS UN SENS.
 *
 * Choisir un piètement amène la bande sur son pack-shot : c'est la réponse
 * visuelle au geste. Parcourir les photos, lui, ne coche rien — on regarde le
 * tabouret sous ses autres piètements sans que le panier bouge sous nos pieds.
 * C'est l'inverse exact de `go-visual-swipe` sur l'accueil, où atteindre une
 * vignette voisine EST la sélection : là-bas on compose, ici on examine. Ne
 * jamais refermer la boucle « défilement → sélection » par symétrie apparente
 * avec l'autre composant.
 *
 * Trois vignettes dans le DOM, jamais plus : [précédente, courante, suivante],
 * recalculées modulo le nombre de vues. C'est ce qui rend la boucle infinie
 * sans clone ni piste géante — la dernière ambiance a pour suivante le premier
 * pack-shot, par construction.
 *
 * Pourquoi `transform` et non `overflow-x` + `scroll-snap` : le défilement
 * natif emporte plusieurs images sur un geste vif, alors que la règle est de
 * s'ancrer sur l'image ADJACENTE quelle que soit la vitesse.
 */
class GoProductGallery extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-gallery-viewport]');
    this.track = this.querySelector('[data-gallery-track]');
    this.thumbs = this.querySelector('[data-gallery-thumbs]');
    this.prevButton = this.querySelector('[data-gallery-prev]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    if (!this.viewport || !this.track) return;

    let data;
    try {
      data = JSON.parse(this.querySelector('[data-gallery-data]')?.textContent || '{}');
    } catch {
      data = {};
    }

    /*
     * Un pack-shot par variante sœur en entrée, une VUE par image distincte en
     * sortie. Le dédoublonnage se fait ici et pas en Liquid parce que le même
     * passage doit produire deux choses à la fois : la liste des vues, et la
     * table « quelle variante mène à quelle vue ». Deux piètements partageant
     * une photo doivent tomber sur la même vignette, pas sur deux jumelles.
     */
    this.shots = [];
    this.shotOf = {};
    (data.shots || []).forEach((shot) => {
      let position = this.shots.findIndex((known) => known.m === shot.m);
      if (position < 0) {
        position = this.shots.length;
        this.shots.push(shot);
      }
      this.shotOf[shot.v] = position;
    });

    this.photos = data.photos || {};
    this.variant = this.dataset.variant;
    if (!this.shots.length) return;

    /*
     * ⚠ Le modal se cherche PAR ID SUR LE DOCUMENT, jamais dans cet element.
     *
     * `ModalDialog.connectedCallback` (global.js) se termine par
     * `document.body.appendChild(this)` : le modal a deja quitte
     * <go-product-gallery> quand ce script differe s'execute. Un
     * `this.querySelector('modal-dialog')` renverrait `null` et la visionneuse
     * ne s'ouvrirait jamais, sans erreur visible. Meme piege que
     * `go-context-photos` et `go-texture-zoom` ; c'est l'idiome de Dawn lui-meme.
     */
    this.modal = document.getElementById(`GoGalleryModal-${this.dataset.section}`) || this.querySelector('modal-dialog');
    // Le plateau a voyage AVEC le modal : le chercher ici reproduirait le meme
    // piege un cran plus bas.
    this.stage = this.modal?.querySelector('[data-gallery-stage]');

    this.views = this.viewsFor(this.variant);
    this.current = this.shotOf[this.variant] || 0;
    this.index = 0;
    this.order = [this.current];

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onNav = this.onNav.bind(this);
    this.onThumb = this.onThumb.bind(this);
    this.onViewportClick = this.onViewportClick.bind(this);

    this.viewport.addEventListener('pointerdown', this.onPointerDown);
    this.viewport.addEventListener('keydown', this.onKeyDown);
    this.viewport.addEventListener('click', this.onViewportClick);
    this.prevButton?.addEventListener('click', this.onNav);
    this.nextButton?.addEventListener('click', this.onNav);
    this.thumbs?.addEventListener('click', this.onThumb);

    // Les chevrons sont rendus masques : sans JS ils ne feraient rien. Une
    // seule vue n'a pas de voisine, ils restent alors absents.
    if (this.views.length > 1) {
      this.prevButton?.removeAttribute('hidden');
      this.nextButton?.removeAttribute('hidden');
    }

    // La rangee de miniatures rendue par le serveur est deja juste : la
    // reconstruire ici remplacerait des noeuds identiques et ferait clignoter
    // les images au chargement. Elle ne sera refaite qu'au changement de
    // pietement, quand les photos d'ambiance peuvent changer.
    this.build({ keepThumbs: true });

    /*
     * On observe le RÉSULTAT, pas le mécanisme.
     *
     * `variantChange` serait le signal naturel, mais il n'arrive pas toujours :
     * `VariantSelects` appelle `dispatchProductSelectEvent()` avant son
     * `publish`, et cet appel touche `window.StandardEvents` (analytics
     * Shopify, chargé en parallèle). Quand il lève, le `publish` n'a jamais
     * lieu. La colonne d'information, elle, est réellement remplacée à chaque
     * changement. Même choix que `go-visual-swipe` et `go-variant-url`.
     */
    this.info = document.getElementById(`ProductInfo-${this.dataset.section}`);
    if (this.info) {
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.info, { childList: true, subtree: true });
    }

    /*
     * La position de la bande est un nombre de pixels, calcule depuis la
     * largeur du cadre. Rien ne la recalculerait apres une rotation de telephone
     * ou le passage d'un palier : elle resterait a l'ancien decalage et
     * laisserait voir deux demi-images. `ResizeObserver` plutot qu'un `resize`
     * de fenetre : le cadre change aussi de largeur quand la colonne bouge.
     */
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.dragging) this.apply(false);
      });
      this.resizeObserver.observe(this.viewport);
    }
  }

  disconnectedCallback() {
    this.clearSettle();
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
    this.viewport?.removeEventListener('keydown', this.onKeyDown);
    this.viewport?.removeEventListener('click', this.onViewportClick);
    this.thumbs?.removeEventListener('click', this.onThumb);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
  }

  /* ------------------------------------------------------ suivi variante */

  /** Les pack-shots du tissu, puis les ambiances de CETTE variante : le
   *  métachamp est porté par la variante, pas par le tissu. */
  viewsFor(variantId) {
    return this.shots.concat(this.photos[variantId] || []);
  }

  /**
   * Le piètement a changé : on amène la bande sur son pack-shot.
   *
   * C'est le seul sens du lien. Rien ici n'est appelé par le geste — voir la
   * note en tête de fichier.
   */
  sync() {
    const script = this.info?.querySelector('[data-selected-variant]');
    if (!script) return;

    let id;
    try {
      id = String(JSON.parse(script.textContent)?.id || '');
    } catch {
      /* Le JSON est remplacé en cours de rendu : la mutation suivante repassera ici. */
      return;
    }
    if (!id || id === this.variant) return;

    // Variante inconnue de la table — un tissu qui aurait changé, ce que cette
    // page ne permet pas. On préfère ne rien faire à sauter sur une vue fausse.
    if (!(id in this.shotOf)) return;

    this.flush();
    this.variant = id;
    this.dataset.variant = id;
    this.views = this.viewsFor(id);
    this.current = this.shotOf[id];
    this.build();
  }

  /* ------------------------------------------------------------ montage */

  /**
   * Repose la bande autour de la vue courante.
   *
   * L'ordre est calcule modulo le nombre de vues : c'est la seule definition de
   * la boucle, et elle tient en une ligne. A deux vues, la precedente et la
   * suivante sont la meme image — le geste marche dans les deux sens sans cas
   * particulier.
   */
  build({ keepThumbs = false } = {}) {
    const total = this.views.length;
    if (this.current >= total) this.current = 0;

    this.order =
      total > 1
        ? [(this.current - 1 + total) % total, this.current, (this.current + 1) % total]
        : [this.current];
    this.index = total > 1 ? 1 : 0;

    this.track.innerHTML = this.order
      .map((view, position) => {
        const entry = this.views[view];
        return (
          `<div class="go-gallery__slide" data-gallery-slide${entry.p ? ' data-packshot' : ''}>` +
          `<img class="go-gallery__image" src="${entry.m}" alt="" ` +
          `loading="${position === this.index ? 'eager' : 'lazy'}" draggable="false">` +
          `</div>`
        );
      })
      .join('');

    this.slides = Array.from(this.track.children);
    this.dataset.active = String(this.current);

    this.apply(false);
    if (keepThumbs) this.syncThumbs();
    else this.renderThumbs();
    this.preload();
  }

  /**
   * Precharge les vues a DEUX crans, pas les voisines immediates : celles-ci
   * sont deja dans le DOM, donc deja demandees. Ce sont les suivantes qui
   * doivent etre pretes quand le doigt enchaine.
   */
  preload() {
    const total = this.views.length;
    if (total < 3) return;
    [(this.current - 2 + total) % total, (this.current + 2) % total].forEach((view) => {
      const preloader = new Image();
      preloader.src = this.views[view].m;
    });
  }

  /** Refait la rangée entière : les ambiances appartiennent à la variante, une
   *  autre variante peut donc en avoir d'autres, ou aucune. */
  renderThumbs() {
    if (!this.thumbs) return;
    const template = this.dataset.thumbLabel || '';
    this.thumbs.innerHTML = this.views
      .map(
        (view, position) =>
          `<li class="go-gallery__thumb-item">` +
          `<a class="go-gallery__thumb" href="${view.f}" data-gallery-thumb="${position}" ` +
          `aria-current="${position === this.current ? 'true' : 'false'}" ` +
          `aria-label="${template.replace('__N__', String(position + 1))}">` +
          `<img class="go-gallery__thumb-image" src="${view.t}" alt="" loading="lazy" draggable="false">` +
          `</a></li>`
      )
      .join('');
    this.revealThumb();
  }

  /** Marque la miniature active sans toucher au reste de la rangée. */
  syncThumbs() {
    if (!this.thumbs) return;
    this.thumbs.querySelectorAll('[data-gallery-thumb]').forEach((link) => {
      link.setAttribute('aria-current', Number(link.dataset.galleryThumb) === this.current ? 'true' : 'false');
    });
    this.revealThumb();
  }

  /**
   * Ramene la miniature active dans la vue.
   *
   * `scrollLeft` et non `scrollIntoView` : ce dernier ferait aussi defiler la
   * page. Meme raison que `GoConfigSteps.reveal()`.
   */
  revealThumb() {
    const active = this.thumbs?.querySelector('[aria-current="true"]');
    if (!active) return;

    const left = active.offsetLeft;
    const right = left + active.offsetWidth;
    const view = this.thumbs.scrollLeft;
    const edge = view + this.thumbs.clientWidth;
    if (left < view) this.thumbs.scrollLeft = left;
    else if (right > edge) this.thumbs.scrollLeft = right - this.thumbs.clientWidth;
  }

  /* -------------------------------------------------------------- geste */

  get width() {
    return this.viewport.clientWidth || 1;
  }

  onPointerDown(event) {
    if (this.views.length < 2 || event.button !== 0) return;

    // Un geste qui arrive pendant qu'une transition se termine : on solde le
    // recentrage en attente tout de suite, sinon il tomberait au milieu du
    // nouveau mouvement et ferait sauter la bande.
    this.flush();
    this.suppressClick = false;

    this.start = event.clientX;
    this.startY = event.clientY;
    this.delta = 0;
    this.dragging = false;

    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    window.addEventListener('pointerup', this.onPointerUp, { once: true });
    window.addEventListener('pointercancel', this.onPointerUp, { once: true });
  }

  onPointerMove(event) {
    if (this.start === undefined) return;

    const dx = event.clientX - this.start;
    const dy = event.clientY - this.startY;

    // Tant que le geste n'est pas franchement horizontal, on ne prend pas la
    // main : un doigt qui part en diagonale veut faire defiler la page.
    if (!this.dragging) {
      if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
      this.dragging = true;
      this.track.classList.add('is-dragging');
    }

    // Aucune resistance aux bords, contrairement a `go-visual-swipe` : la bande
    // boucle, il n'y a pas de bord.
    this.delta = dx;
    this.apply(false, this.delta);
  }

  onPointerUp() {
    window.removeEventListener('pointermove', this.onPointerMove);
    if (this.start === undefined) return;

    const moved = this.delta || 0;
    this.start = undefined;
    this.track.classList.remove('is-dragging');

    if (!this.dragging) return;
    this.dragging = false;

    // Le `click` qui suit un glissement ne doit pas ouvrir la visionneuse. Le
    // drapeau est leve jusqu'au prochain `pointerdown` plutot que remis a zero
    // par un minuteur : l'ordre entre `click` et un `setTimeout(0)` n'est pas
    // garanti.
    this.suppressClick = true;

    // Le geste a eu lieu : les chevrons s'estompent, leur role d'annonce est
    // rempli. Ils restent cliquables.
    this.classList.add('has-swiped');

    // Un dixieme de la largeur suffit, et JAMAIS plus d'une image a la fois :
    // la vitesse n'entre pas dans le calcul.
    const step = Math.abs(moved) > this.width * 0.1 ? -Math.sign(moved) : 0;
    if (step === 0) {
      this.apply(true);
      return;
    }
    this.go(this.index + step);
  }

  onKeyDown(event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    this.flush();
    this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
  }

  /** Le chevron ne fait rien de plus que le geste : il vise la vue voisine. */
  onNav(event) {
    this.flush();
    this.go(this.index + (event.currentTarget === this.nextButton ? 1 : -1));
  }

  /**
   * Miniature : acces direct a une vue. Le lien reste un vrai lien vers l'image
   * en grand — c'est le repli sans JS — et n'est detourne que quand le
   * composant tourne.
   */
  onThumb(event) {
    const link = event.target.closest('[data-gallery-thumb]');
    if (!link) return;
    event.preventDefault();

    const target = Number(link.dataset.galleryThumb);
    if (!Number.isFinite(target) || target === this.current) return;
    this.flush();
    this.current = target;
    this.build({ keepThumbs: true });
  }

  onViewportClick() {
    if (this.suppressClick) {
      this.suppressClick = false;
      return;
    }
    this.open(this.current);
  }

  /* -------------------------------------------------------- visionneuse */

  open(view) {
    const entry = this.views[view];
    if (!entry || !this.stage || !this.modal) return;

    // L'image est recreee a chaque ouverture : reutiliser le meme noeud
    // laisserait la photo precedente a l'ecran pendant le chargement.
    const image = new Image();
    image.className = 'go-gallery__full';
    image.alt = '';
    image.src = entry.f;
    this.stage.replaceChildren(image);

    // `show(opener)` rend le focus au declencheur a la fermeture : c'est le
    // contrat de <modal-dialog>, on ne le contourne pas.
    this.modal.show(this.viewport);
  }

  /* ----------------------------------------------------------- position */

  /**
   * Anime vers une vignette voisine, puis recentre la bande sur elle.
   *
   * Le recentrage est invisible : l'image qui prend la place du milieu est
   * exactement celle qu'on vient d'atteindre. C'est lui qui rend la boucle
   * infinie — apres chaque pas, la bande retrouve une precedente et une
   * suivante.
   */
  go(target) {
    if (this.views.length < 2) return;

    const clamped = Math.max(0, Math.min(target, this.slides.length - 1));
    if (clamped === this.index) {
      this.apply(true);
      return;
    }

    this.index = clamped;
    this.apply(true);
    this.settle(this.order[clamped]);
  }

  settle(next) {
    this.clearSettle();

    const finish = () => {
      this.clearSettle();
      this.current = next;
      this.build({ keepThumbs: true });
    };

    this.settleHandler = finish;
    this.track.addEventListener('transitionend', finish, { once: true });

    /*
     * Filet : `transitionend` ne se declenche pas quand il n'y a pas eu de
     * transition — c'est le cas sous « reduire les animations », ou la feuille
     * la coupe. Sans ce minuteur la bande resterait decalee d'un cran, avec une
     * vue vide au bord suivant.
     */
    this.settleTimer = setTimeout(finish, 420);
  }

  clearSettle() {
    if (this.settleTimer) {
      clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }
    if (this.settleHandler) {
      this.track.removeEventListener('transitionend', this.settleHandler);
      this.settleHandler = null;
    }
  }

  /** Solde un recentrage en attente, tout de suite. */
  flush() {
    this.settleHandler?.();
  }

  apply(animated, offset = 0) {
    this.track.style.transition = animated ? '' : 'none';
    this.track.style.transform = `translate3d(${-this.index * this.width + offset}px, 0, 0)`;
    if (animated) return;

    // Pendant le geste, rien a forcer : `is-dragging` coupe deja la transition
    // en CSS, et lire `offsetWidth` a chaque `pointermove` serait un reflow
    // synchrone par image — exactement ce que ce geste cherche a eviter.
    if (this.dragging) return;

    // Force le recalcul avant de rendre la transition, sinon le retour a la
    // position animee reprend l'ancienne valeur et le saut se voit.
    void this.track.offsetWidth;
    this.track.style.transition = '';
  }
}

// Meme garde que les autres composants GO! : deux balises <script src>
// identiques s'executeraient deux fois.
if (!customElements.get('go-product-gallery')) {
  customElements.define('go-product-gallery', GoProductGallery);
}
